import { useMutation, useQuery } from "@apollo/client/react";

import {
  DraftTemplateParagraphEditDocument,
  ProjectDraftTemplateParagraphDocument,
} from "@app_schema";

import { track } from "@application/utilities/track";

import { type ParagraphRun } from "@styled/paragraph_editor_runs";

import { type DraftParagraphEdit } from "./use_draft_paragraph_edit";
import { useParagraphEditing } from "./use_paragraph_editing";

// Live editing on his template's Word file: the paragraph he clicked, read
// fresh from it, and his edit saved on Done against the file he saw
// (`fileChecksum`). The template then redraws with it.
export const useTemplateParagraphEdit = (
  projectId: string,
  fileChecksum: string | undefined,
): DraftParagraphEdit => {
  const [save, saveState] = useMutation(DraftTemplateParagraphEditDocument, {
    refetchQueries: ["ProjectDraftingTemplateEdit"],
    awaitRefetchQueries: true,
  });
  const { done, ...state } = useParagraphEditing(saveState.reset);
  const block =
    state.editing && "block" in state.editing ? state.editing.block : null;
  const { data } = useQuery(ProjectDraftTemplateParagraphDocument, {
    variables: { id: projectId, block: block ?? 0 },
    skip: block === null,
    fetchPolicy: "network-only",
  });
  const paragraph = data?.project.draftingTemplateEdit?.draftParagraph;
  const current = paragraph?.block === block ? paragraph : undefined;
  const shown = current && !current.lock ? current.runs : undefined;

  return {
    ...state,
    runs: shown,
    saving: saveState.loading,
    error: saveState.error?.message,
    done: (runs: ParagraphRun[]) =>
      done(shown, runs, () => {
        if (block === null || !fileChecksum) return;
        return save({
          variables: { id: projectId, input: { block, fileChecksum, runs } },
        }).then(({ data }) => {
          const changed = data?.project.draftTemplateParagraphEdit.changed;
          if (changed) {
            track("drafting_paragraph_edited", {
              project_id: projectId,
              target: "template",
            });
          }
          return changed;
        });
      }),
  };
};
